import React, { useEffect, useState } from 'react';
import { push as pushToHistory } from 'redux-first-history';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GIButton from '../../../components/common/button/GIButton';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

// Hooks
import { useDebounce } from '../../common/useDebounce';

// Components
import { ValidationErrorMessage } from '../../../components/common/form';

// Action creator
import { assigneesActionCreators } from '../../../actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';

// type
import { IManageWorkgroup } from './manageWorkgroup.interface';
import { VALIDATION_TYPE } from './manageWorkgroupTags.constant';
import { SPECIAL_CHARACTERS_REGEX } from '../../../utils/validators/regex';

const ManageWorkgroup = (props: IManageWorkgroup) => {
    const [isEditWorkGroup, setEditWorkgroup] = useState<boolean>(false);
    const { item, setUpdateEvent, validateWorkgroupName, updateWorkgroup } =
        props;
    const workgroupName = item?.workGroupName;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty, isValid },
        reset,
        clearErrors,
        setError,
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            id: '',
            workgroupName: '',
            isDefault: '',
        },
    });

    const workgroupValue: string = watch('workgroupName');
    const debounceSearchValue: string = useDebounce(workgroupValue, 2000);

    useEffect(() => {
        reset(resetFormState());
    }, [item]);

    useEffect(() => {
        if (
            debounceSearchValue?.length > 0 &&
            isEditWorkGroup &&
            debounceSearchValue !== workgroupName
        ) {
            validateWorkgroup(debounceSearchValue);
        }
    }, [debounceSearchValue]);

    const validateWorkgroup = async (searchValue: string) => {
        const notCustomValidation = [
            VALIDATION_TYPE.MIN_LENGTH,
            VALIDATION_TYPE.REQUIRED,
            VALIDATION_TYPE.ALLOW_CHAR_AND_NUMBER,
        ];
        const errorType = errors?.workgroupName?.type || '';
        if (!notCustomValidation.includes(errorType)) {
            const response = await validateWorkgroupName(searchValue);
            if (response) {
                setError('workgroupName', {
                    type: 'duplicateWorkgroupName',
                    message: 'ticket_management.workgroup.error.duplicateField',
                });
            } else if (
                errors?.workgroupName?.type ===
                VALIDATION_TYPE.DUPLICATE_WORKGROUP_NAME
            ) {
                clearErrors('workgroupName');
            }
        }
    };

    const cancelModified = () => {
        setEditWorkgroup(false);
        clearErrors();
        reset(resetFormState());
    };

    const resetFormState = () => {
        return {
            id: item.id.toString(),
            workgroupName: workgroupName,
            isDefault: item.defaultWorkGroup,
        };
    };

    const onSubmit = async (submitData: any) => {
        const payload = {
            id: submitData['id'],
            name: submitData['workgroupName'],
            isDefault: submitData['isDefault'],
            roleId: '',
            csrftoken: window.csrf_token,
        };
        const response = await updateWorkgroup(payload);
        if (response) {
            setUpdateEvent(true);
            setEditWorkgroup(false);
            reset(resetFormState());
        }
    };

    const getModifiedReadView = () => {
        if (isEditWorkGroup) {
            return (
                <form onSubmit={handleSubmit(onSubmit)} data-testid="manage-tags-edit-form">
                    <div
                        className="manage-workgroup-ui margin-bottom-2em margin-top-2em"
                        id="manage-workgroup"
                        data-testid="manage-tags-edit-form-container"
                    >
                        <div className="desktop:grid-col-6 text-left" data-testid="manage-tags-edit-form-input-error-container">
                            <div className={'gi-component padding-y-3'} data-testid="manage-tags-edit-form-input">
                                <input
                                    id={'workgroupname_input'}
                                    data-testid="manage-tags-edit-form-input-field"
                                    className={'gi-input__input grid-col-9'}
                                    {...register('workgroupName', {
                                        required: {
                                            value: true,
                                            message:
                                                'ticket_management.workgroup.error.required',
                                        },
                                        minLength: {
                                            value: 5,
                                            message:
                                                'ticket_management.workgroup.error.min',
                                        },
                                        validate: {
                                            allowCharAndNumber: (value) => {
                                                return (
                                                    !SPECIAL_CHARACTERS_REGEX.test(value) ||
                                                    'ticket_management.workgroup.error.specialCharacter'
                                                );
                                            },
                                        },
                                    })}
                                />
                            </div>
                            <ValidationErrorMessage
                                isVisible={errors.workgroupName}
                                messageText={errors.workgroupName?.message}
                            />
                        </div>
                        <div className="desktop:grid-col-6 text-right" data-testid="manage-tags-edit-form-controls">
                            <span className="controls" data-testid="manage-tags-edit-form-controls-span">
                                <GIButton
                                    id="cancelModifiedWorkgroup"
                                    dataTestId="manage-tags-edit-form-cancel-button"
                                    labelId={'button.cancel'}
                                    variant="secondary"
                                    onClickHandler={() => cancelModified()}
                                />
                                <GIButton
                                    id="saveChangesWorkgroup"
                                    dataTestId='manage-tags-edit-form-save-button'
                                    labelId={'button.save_changes'}
                                    isDisabled={!isDirty || !isValid}
                                    type="submit"
                                />
                            </span>
                        </div>
                    </div>
                </form>
            );
        } else {
            return (
                <div
                    className="manage-workgroup-ui margin-bottom-2em margin-top-2em"
                    id="view-workgroup"
                    data-testid="manage-tags-edit-view-workgropup-container"
                >
                    <div className="desktop:grid-col-4 text-left" data-testid="manage-tags-edit-view-workgropup-heading">
                        <h3 data-testid="manage-tags-edit-view-workgropup-h3" className='workgroup-name' title={workgroupName}>{workgroupName}</h3>
                    </div>
                    <div className="desktop:grid-col-6 text-left" data-testid="manage-tags-edit-view-workgropup-edit-btn">
                        <GIButton
                            id="viewManageWorkgroupTagsEdit"
                            labelId={'button.edit'}
                            onClickHandler={() => setEditWorkgroup(true)}
                            iconPlacement="start"
                            variant="secondary"
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            dataTestId="manage-tags-edit-view-workgropup-edit-btn"
                        />
                    </div>
                </div>
            );
        }
    };

    return getModifiedReadView();
};

export default connect(null, {
    ...assigneesActionCreators,
    pushToHistory,
})(ManageWorkgroup);
